// clone-repo-with-recovery-menu.ts
//
// v3: Wrap clone repo vào src/ với recovery menu khi gặp vấn đề (repo not found,
// permission denied, network). Thay vì để git văng lỗi thô + crash, verify remote
// TRƯỚC clone → classify lỗi → menu: retry / nhập URL khác / switch account / bỏ.
//
// Cũng dọn thư mục src/<name> dở nếu clone fail giữa chừng (tránh "đã tồn tại"
// khi user retry).

import { join } from "node:path";
import type { AddRepoFlags } from "./add-repo-non-interactive-flags.js";
import {
  type CloneRepoResult,
  cloneCodeRepoIntoSrc,
  RepoAlreadyInWorkspaceError,
} from "./clone-code-repo-into-src.js";
import { removeRecursive } from "./filesystem-helpers.js";
import { isGithubHost, parseGitRemoteHost } from "./git-remote-host.js";
import { isNonInteractive, MissingFlagError } from "./non-interactive-context.js";
import { promptAndStorePatForHost } from "./prompt-and-store-pat-for-non-github-host.js";
import { confirmOrFlag, inputOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";
import { triggerGhCliAuthLogin } from "./trigger-gh-cli-auth-login.js";
import { tryVerifyGitRemoteAccessible } from "./verify-git-remote-accessible.js";

// Hướng dẫn theo từng loại lỗi remote.
function explainRemoteFailure(url: string, reason: string, detail?: string): void {
  log.warn(`⛔ Không clone được ${url}`);
  switch (reason) {
    case "not-found":
      log.dim("  → Repo không tồn tại (sai URL, hoặc repo chưa được tạo trên GitHub).");
      break;
    case "no-access":
      log.dim(
        "  → Không có quyền truy cập (repo private + sai account), HOẶC repo không tồn tại.\n" +
          "    Kiểm tra: đang login đúng GitHub account chưa? (gh auth status)",
      );
      break;
    case "network":
      log.dim("  → Lỗi mạng (mất kết nối / DNS / timeout).");
      break;
    default:
      log.dim(`  → Lỗi không xác định${detail ? `: ${detail}` : ""}.`);
  }
}

// Switch account → dùng triggerGhCliAuthLogin authoritative (đủ flags --hostname
// --git-protocol ssh, nhất quán với init flow). Catch để warn-only (recovery loop
// tự re-verify sau).
function switchGhAccount(): void {
  log.info("Mở browser để switch GitHub account...");
  try {
    triggerGhCliAuthLogin();
  } catch (err) {
    log.warn(
      `${err instanceof Error ? err.message : err}. Có thể chạy 'gh auth login' tay rồi retry.`,
    );
  }
}

const ADD = "add repo";
const CLONE_FAIL_FLAG_SPEC = {
  flag: "--on-clone-fail",
  command: ADD,
  hint: "--on-clone-fail abort|retry|skip",
};
const EXISTING_DIR_FLAG_SPEC = {
  flag: "--on-existing-dir",
  command: ADD,
  hint: "--on-existing-dir abort|use|backup",
};
const NEW_DIR_NAME_FLAG_SPEC = { flag: "--name", command: ADD, hint: "--name ten-thu-muc-khac" };
const NEW_URL_FLAG_SPEC = { flag: "--url", command: ADD, hint: "--url <url-khac>" };

// Menu recovery khi clone fail. Trả action user chọn.
// url dùng để detect host → quyết định offer PAT (non-GitHub) hay switch-account (GitHub).
async function promptCloneRecovery(
  reason: string,
  url: string,
  flags?: AddRepoFlags,
): Promise<"retry" | "new-url" | "switch-account" | "enter-pat" | "skip"> {
  // Non-interactive: menu recovery không có ai bấm. `--on-clone-fail` quyết định,
  // mặc định `abort` — tự `retry`/`skip` khi không ai nhìn là cách tạo trạng thái
  // nửa vời trên đĩa (thư mục clone dở, manifest lệch).
  if (flags) {
    const action = flags.onCloneFail ?? "abort";
    if (action === "abort") {
      throw new MissingFlagError({
        missingFlag: "--on-clone-fail",
        command: "add repo",
        hint: "--on-clone-fail retry|skip (mặc định abort — dừng, không để lại thư mục dở)",
      });
    }
    return action === "skip" ? "skip" : "retry";
  }
  const host = parseGitRemoteHost(url);
  const isGh = isGithubHost(host);

  const choices: Array<{ name: string; value: string }> = [
    { name: "Thử lại (retry)", value: "retry" },
    { name: "Nhập URL repo khác", value: "new-url" },
  ];

  const authReasons = new Set(["no-access", "unknown", "timeout"]);

  if (authReasons.has(reason)) {
    if (isGh) {
      // GitHub: offer switch account qua gh auth login
      choices.push({
        name: "Switch GitHub account (gh auth login — mở browser)",
        value: "switch-account",
      });
    } else if (host) {
      // Non-GitHub (GitLab, Bitbucket, Gitea, ...): offer nhập PAT
      choices.push({
        name: `Nhập Personal Access Token cho ${host} (lưu vào keychain)`,
        value: "enter-pat",
      });
    }
  }

  choices.push({ name: "Bỏ qua repo này", value: "skip" });

  return (await selectOrFlag(undefined, CLONE_FAIL_FLAG_SPEC, {
    message: "Cách xử lý?",
    choices,
  })) as "retry" | "new-url" | "switch-account" | "enter-pat" | "skip";
}

export interface CloneWithRecoveryResult {
  cloned: CloneRepoResult | null; // null nếu user skip.
  skipped: boolean;
}

// Clone repo vào src/<name> với recovery menu. url/name có thể đổi qua menu.
// Trả { cloned, skipped } — skipped=true nếu user chọn "Bỏ qua repo này".
export async function cloneRepoWithRecovery(args: {
  workspaceRoot: string;
  url: string;
  name: string;
  /** Cờ non-interactive (T3). `undefined` = có người ngồi trước máy. */
  nonInteractiveFlags?: AddRepoFlags;
}): Promise<CloneWithRecoveryResult> {
  let url = args.url;
  let name = args.name;
  const flags = args.nonInteractiveFlags;

  while (true) {
    // 1. Verify remote accessible TRƯỚC clone (tránh git văng lỗi thô + thư mục dở).
    const verify = tryVerifyGitRemoteAccessible(url);
    if (!verify.ok) {
      explainRemoteFailure(url, verify.reason ?? "unknown", verify.detail);
      const action = await promptCloneRecovery(verify.reason ?? "unknown", url, flags);

      if (action === "skip") {
        log.dim(`Bỏ qua repo ${name}.`);
        return { cloned: null, skipped: true };
      }
      if (action === "switch-account") {
        switchGhAccount();
        continue; // re-verify với account mới.
      }
      if (action === "enter-pat") {
        const host = parseGitRemoteHost(url);
        if (host) {
          await promptAndStorePatForHost(host);
        }
        continue; // re-verify — git credential store mới sẽ được dùng.
      }
      if (action === "new-url") {
        url = await inputOrFlag(undefined, NEW_URL_FLAG_SPEC, {
          message: "URL repo khác:",
          validate: (v) => (v.trim().length > 0 ? true : "URL bắt buộc"),
        });
        // Hỏi đổi tên không (URL mới có thể tên khác).
        const renameToo = await confirmOrFlag(undefined, NEW_DIR_NAME_FLAG_SPEC, {
          message: `Đổi tên thư mục trong src/ theo URL mới? (hiện: ${name})`,
          default: false,
        });
        if (renameToo) {
          const { inferRepoNameFromUrl } = await import("./clone-code-repo-into-src.js");
          name = await inputOrFlag(undefined, NEW_DIR_NAME_FLAG_SPEC, {
            message: "Tên thư mục:",
            default: inferRepoNameFromUrl(url),
          });
        }
        continue;
      }
      // action === "retry" → loop lại verify.
      continue;
    }

    // 2. Remote OK → clone. Vẫn try/catch phòng clone fail sau verify (race).
    try {
      const cloned = await cloneCodeRepoIntoSrc({ workspaceRoot: args.workspaceRoot, url, name });
      return { cloned, skipped: false };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);

      // CRITICAL: lỗi PRE-CLONE (repo/tên đã tồn tại) → repo cũ KHÔNG bị git clone
      // đụng → TUYỆT ĐỐI KHÔNG xóa src/<name> (sẽ xóa nhầm repo cũ → mất data).
      // Báo lỗi + cho user đổi tên / bỏ qua. KHÔNG dọn thư mục.
      if (err instanceof RepoAlreadyInWorkspaceError) {
        // Repo đã tồn tại CÙNG remote = đã add rồi → CHẶN, không cho clone bản 2
        // (tránh 2 folder cùng remote → push đè nhau). Báo rõ + skip thẳng.
        const existingUrl = await readExistingRepoUrl(args.workspaceRoot, name);
        if (existingUrl && sameRemote(existingUrl, url)) {
          log.warn(
            `⚠ Repo này đã có trong workspace (src/${name}). Dùng trực tiếp, không add lại.`,
          );
          log.dim(`  Muốn thay bằng bản mới? Gỡ trước: avatar remove repo ${name}`);
          return { cloned: null, skipped: true };
        }

        // Trùng TÊN nhưng KHÁC remote (repo khác) → đổi tên là hợp lý.
        log.warn(`⚠ ${message}`);
        // Non-interactive: `--on-existing-dir` thay menu. `abort` (mặc định) ném
        // lỗi ⇒ thư mục cũ NGUYÊN VẸN, `repos.json` không đổi.
        if (flags) {
          const onExisting = flags.onExistingDir ?? "abort";
          if (onExisting === "abort") {
            throw new MissingFlagError({
              missingFlag: "--on-existing-dir",
              command: "add repo",
              hint: "--on-existing-dir use|backup (mặc định abort — giữ nguyên thư mục cũ)",
            });
          }
          if (onExisting === "use") return { cloned: null, skipped: true };
        }
        const action = await selectOrFlag(flags ? "rename" : undefined, EXISTING_DIR_FLAG_SPEC, {
          message: "Cách xử lý?",
          choices: [
            { name: "Đổi tên thư mục khác", value: "rename" },
            { name: "Bỏ qua repo này", value: "skip" },
          ],
        });
        if (action === "skip") return { cloned: null, skipped: true };
        name = await inputOrFlag(
          flags ? `${name}-backup-${Date.now()}` : undefined,
          NEW_DIR_NAME_FLAG_SPEC,
          {
            message: "Tên thư mục mới trong src/:",
            validate: (v) => (v.trim().length > 0 ? true : "Tên bắt buộc"),
          },
        );
        continue;
      }

      // Lỗi clone THẬT (git tạo src/<name> dở rồi fail) → dọn thư mục dở để retry sạch.
      log.warn(`Clone fail: ${message}`);
      await removeRecursive(join(args.workspaceRoot, "src", name)).catch(() => {});

      const action = await promptCloneRecovery("unknown", url, flags);
      if (action === "skip") return { cloned: null, skipped: true };
      if (action === "switch-account") {
        switchGhAccount();
        continue;
      }
      if (action === "enter-pat") {
        const host = parseGitRemoteHost(url);
        if (host) {
          await promptAndStorePatForHost(host);
        }
        continue; // re-verify sau khi lưu credential.
      }
      if (action === "new-url") {
        url = await inputOrFlag(undefined, NEW_URL_FLAG_SPEC, {
          message: "URL repo khác:",
          validate: (v) => (v.trim().length > 0 ? true : "URL bắt buộc"),
        });
      }
      // retry / new-url → loop lại while(true).
    }
  }
}

// Đọc URL remote của repo đã có trong manifest (so sánh xem có cùng repo không).
async function readExistingRepoUrl(workspaceRoot: string, name: string): Promise<string | null> {
  const { readReposManifest } = await import("./workspace-repos-manifest-store.js");
  const repos = await readReposManifest(workspaceRoot);
  return repos.find((r) => r.name === name)?.url ?? null;
}

// So sánh 2 git URL có trỏ cùng repo không. Chuẩn hóa: bỏ scheme (ssh/https),
// .git suffix, trailing slash, lowercase. "git@github.com:o/r.git" ==
// "https://github.com/o/r/" → cùng repo.
function sameRemote(a: string, b: string): boolean {
  const norm = (u: string) =>
    u
      .trim()
      .toLowerCase()
      .replace(/^git@/, "")
      .replace(/^https?:\/\//, "")
      .replace(/:/, "/") // git@github.com:o/r → github.com/o/r
      .replace(/\.git$/, "")
      .replace(/\/+$/, "");
  return norm(a) === norm(b);
}
